import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../components/ProductCard";

export default function Checkout() {
  const { t } = useTranslation();
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user && user !== false ? user.name : "",
    email: user && user !== false ? user.email : "",
    phone: "", address: "", city: "", country: "France", postal_code: "", notes: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateForm = () => {
    if (items.length === 0) return t("checkout.cart_empty");
    if (!form.full_name || !form.email || !form.phone || !form.address || !form.city || !form.country) {
      return "Veuillez remplir tous les champs obligatoires.";
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const err = validateForm();
    if (err) { setError(err); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, total, items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })) };
      const { data } = await api.post("/orders", payload);
      clear();
      setConfirmed(data);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSubmitting(false);
    }
  };

  const payByFedapay = async () => {
    setError("");
    const err = validateForm();
    if (err) { setError(err); return; }
    setPaying(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity })),
      };
      const { data } = await api.post("/payments/fedapay/create", payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Session FedaPay invalide.");
        setPaying(false);
      }
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
      setPaying(false);
    }
  };

  const payByPaypal = async () => {
    setError("");
    const err = validateForm();
    if (err) { setError(err); return; }
    setPaying(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        ...form,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity })),
      };
      const { data } = await api.post("/payments/paypal/create", payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError("Session PayPal invalide.");
        setPaying(false);
      }
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
      setPaying(false);
    }
  };

  if (confirmed) {
    return (
      <div className="pt-32 md:pt-40 pb-24">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <div className="overline mb-5">{t("checkout.overline")}</div>
          <h1 className="font-serif text-5xl md:text-6xl text-[#F5F5F0] mb-6">
            {t("checkout.success_title_1")} <em className="text-[#D4AF37] not-italic">{confirmed.full_name}</em>.
          </h1>
          <p className="text-[#A3A39D] leading-relaxed mb-3">
            {t("checkout.success_p1", { id: confirmed.id.slice(0, 8).toUpperCase() })}
          </p>
          <p className="text-[#A3A39D] leading-relaxed mb-10">{t("checkout.success_p2")}</p>
          <Link to="/boutique" className="btn-primary" data-testid="back-to-shop-btn">{t("checkout.back_to_shop")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="overline mb-5">{t("checkout.overline")}</div>
        <h1 className="font-serif text-5xl md:text-7xl text-[#F5F5F0] leading-[0.95] mb-4">
          {t("checkout.title_1")} <em className="text-[#D4AF37] not-italic">{t("checkout.title_2")}</em>
        </h1>
        <p className="text-[#A3A39D] max-w-2xl mb-16">{t("checkout.lede")}</p>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-testid="checkout-form">
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label={t("checkout.full_name")} value={form.full_name} onChange={set("full_name")} required testid="fn" />
            <Field label={t("checkout.email")} type="email" value={form.email} onChange={set("email")} required testid="email" />
            <Field label={t("checkout.phone")} value={form.phone} onChange={set("phone")} required testid="phone" />
            <Field label={t("checkout.city")} value={form.city} onChange={set("city")} required testid="city" />
            <div className="md:col-span-2">
              <Field label={t("checkout.address")} value={form.address} onChange={set("address")} required testid="address" />
            </div>
            <Field label={t("checkout.country")} value={form.country} onChange={set("country")} required testid="country" />
            <Field label={t("checkout.postal")} value={form.postal_code} onChange={set("postal_code")} testid="postal" />
            <div className="md:col-span-2">
              <label className="lux-label">{t("checkout.notes")}</label>
              <textarea className="lux-input min-h-[100px]" value={form.notes} onChange={set("notes")} placeholder={t("checkout.notes_placeholder")} data-testid="checkout-notes" />
            </div>

            {error && <div data-testid="checkout-error" className="md:col-span-2 text-sm text-red-400 border border-red-900/60 bg-red-900/10 p-3">{error}</div>}

            <div className="md:col-span-2 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={payByPaypal}
                disabled={paying || submitting}
                data-testid="pay-by-paypal-btn"
                className="disabled:opacity-60 inline-flex items-center gap-2 px-6 py-3 bg-[#FFC439] text-[#003087] text-sm font-semibold tracking-wide hover:bg-[#f0b428] transition-colors"
              >
                <span className="italic font-bold">Pay</span><span className="italic font-bold text-[#009cde] -ml-2">Pal</span>
              </button>
              <button
                type="button"
                onClick={payByFedapay}
                disabled={paying || submitting}
                data-testid="pay-by-fedapay-btn"
                className="disabled:opacity-60 inline-flex items-center gap-2 px-6 py-3 bg-[#0E8345] text-white text-sm font-semibold tracking-wide hover:bg-[#0b6b38] transition-colors"
              >
                <Lock size={14} strokeWidth={1.5} />
                {paying ? "Redirection…" : "FedaPay · Mobile Money"}
              </button>
              <button
                type="submit"
                disabled={submitting || paying}
                data-testid="submit-order-btn"
                className="btn-ghost disabled:opacity-60"
              >
                {submitting ? t("checkout.submitting") : "Commander via la conciergerie"}
              </button>
            </div>

            <div className="md:col-span-2 mt-2">
              <PaymentTrustRow />
            </div>
          </div>

          <aside className="bg-[#151516] p-8 h-fit border border-white/10">
            <div className="overline mb-5">{t("checkout.selection")}</div>
            {items.length === 0 ? (
              <p className="text-sm text-[#A3A39D]">{t("checkout.cart_empty")}</p>
            ) : (
              items.map((i) => (
                <div key={i.product_id} className="flex justify-between gap-4 py-3 border-b border-white/10">
                  <div className="text-sm">
                    <div className="text-[#F5F5F0]">{i.name}</div>
                    <div className="text-[#A3A39D] text-xs">× {i.quantity}</div>
                  </div>
                  <div className="text-sm text-[#D4AF37] whitespace-nowrap">{formatPrice(i.price * i.quantity)}</div>
                </div>
              ))
            )}
            <div className="flex justify-between pt-6">
              <span className="uppercase tracking-[0.18em] text-sm">{t("cart.total")}</span>
              <span className="font-serif text-2xl text-[#D4AF37]">{formatPrice(total)}</span>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="lux-label">{label}{required ? " *" : ""}</label>
      <input type={type} value={value} onChange={onChange} required={required} className="lux-input" data-testid={`checkout-${testid}`} />
    </div>
  );
}

function PaymentTrustRow() {
  return (
    <div data-testid="payment-trust-row" className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 pt-6 border-t border-white/10">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#A3A39D]">
        <Lock size={12} strokeWidth={1.5} className="text-[#D4AF37]" />
        Paiement sécurisé&nbsp;— PayPal · FedaPay
      </div>
      <div className="flex items-center gap-3">
        <CardBadge label="PayPal" />
        <CardBadge label="Mobile Money" />
        <CardBadge label="VISA" />
        <CardBadge label="Mastercard" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A39D] sm:ml-auto">
        SSL&nbsp;· 3D Secure
      </div>
    </div>
  );
}

function CardBadge({ label }) {
  return (
    <span className="inline-flex items-center justify-center px-3 py-1.5 border border-white/15 bg-[#0a0a0b] text-[#F5F5F0] text-[10px] tracking-[0.18em] uppercase font-medium">
      {label}
    </span>
  );
}
